import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import { useCallback, useState } from 'react';
import { Organization } from '../types/models';
import { User } from '../types/models/organization.model';
import { APIResponse } from '../types/services/api-service.type';

interface UseOrganization {
  organization: Organization | null;
  isLoading: boolean;
  isFetching: boolean;
  createOrganization: (data: Partial<Organization>) => Promise<APIResponse>;
  updateOrganization: (
    id: string,
    data: Partial<Organization>
  ) => Promise<APIResponse<Organization>>;
  uploadOrganizationLogo: (id: string, file: File) => Promise<APIResponse>;
  fetchOrganization: (organizationId: string) => void;
}
const useOrganization = (): UseOrganization => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const httpClient = useHttpClient();

  const { showToast } = useToast();

  const fetchOrganization = useCallback(
    (organizationId: string) => {
      const fetchData = async () => {
        setIsFetching(true);
        try {
          const organization = await httpClient.get<Organization>(
            `/organizations/${organizationId}`
          );
          setOrganization(organization);
        } catch (error) {
          showToast(`Error fetching organization: ${error}`, 'error');
        } finally {
          setIsFetching(false);
        }
      };
      fetchData();
    },
    [httpClient, showToast]
  );

  const createOrganization = useCallback(
    async (data: Partial<Organization>) => {
      setIsLoading(true);
      try {
        await httpClient.post<User>('/organizations', data, {
          headers: { Authorization: undefined },
        });

        showToast('Organization created successfully', 'success');

        return { success: true };
      } catch (error) {
        showToast(`Error creating organization: ${error}`, 'error');
        return { success: false, error: error as string };
      } finally {
        setIsLoading(false);
      }
    },
    [httpClient, showToast]
  );

  const updateOrganization = useCallback(
    async (id: string, data: Partial<Organization>) => {
      setIsLoading(true);
      try {
        const response = await httpClient.put<Organization>(
          `/organizations/${id}`,
          data
        );
        fetchOrganization(id);
        return { success: true, data: response };
      } catch (error) {
        showToast(`Error updating organization: ${error}`, 'error');
        return { success: false, error: error as string };
      } finally {
        setIsLoading(false);
      }
    },
    [fetchOrganization, httpClient, showToast]
  );

  const uploadOrganizationLogo = useCallback(
    async (id: string, file: File) => {
      setIsLoading(true);
      try {
        const metadataResponse = await httpClient.post<{
          preSignedUrl: string;
          filekeyId: string;
          fileKey: string;
        }>(`/organizations/${id}/logo`, {
          filename: file.name,
          contentType: file.type,
          uploadType: 'ORGANIZATION_LOGO',
        });

        await fetch(metadataResponse.preSignedUrl, {
          body: file,
          method: 'PUT',
        });

        return { success: true };
      } catch (error) {
        showToast(`Error uploading organization logo: ${error}`, 'error');
        return { success: false, error: error as string };
      } finally {
        setIsLoading(false);
      }
    },
    [httpClient, showToast]
  );

  return {
    organization,
    isLoading,
    isFetching,
    createOrganization,
    updateOrganization,
    uploadOrganizationLogo,
    fetchOrganization,
  };
};

export default useOrganization;
